import { db, storage, functions } from './firebase';
import { collection, getDocs, doc, getDoc, query, orderBy, limit } from 'firebase/firestore';
import { ref, getBytes } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';

// --- Admin cache (Firebase Storage) ---

async function fetchJsonFromStorage(path) {
  try {
    const bytes = await getBytes(ref(storage, path));
    const text = new TextDecoder().decode(bytes);
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export async function loadUserAvatarCache() {
  const data = await fetchJsonFromStorage('admin-cache/user-avatars.json');
  return data || {};
}

export async function loadUserStatsCache() {
  return await fetchJsonFromStorage('admin-cache/user-stats.json');
}

export async function refreshUserAvatars() {
  const fn = httpsCallable(functions, 'refreshUserAvatars');
  const result = await fn();
  return result.data;
}

export async function refreshUserStats() {
  const fn = httpsCallable(functions, 'refreshUserStats');
  const result = await fn();
  return result.data;
}

const USERS_CACHE_MS = 10 * 60 * 1000;
let usersCache = null;

export async function getAllUsers(includeStats = false, { force = false } = {}) {
  if (!includeStats && !force && usersCache && Date.now() - usersCache.at < USERS_CACHE_MS) return usersCache.users;

  const usersRef = collection(db, 'users');
  const snapshot = await getDocs(usersRef);

  if (!includeStats) {
    // Fast mode: only main user document data
    const users = snapshot.docs.map(userDoc => {
      const userData = userDoc.data();
      return {
        id: userDoc.id,
        name: userData.displayName || userData.username || 'Unknown',
        email: userData.email || 'No email',
        createdAt: userData.createdAt,
        streakCount: userData.streakCount || 0,
        lastActive: userData.lastCompletionDate || null,
      };
    });
    usersCache = { users, at: Date.now() };
    return users;
  }

  // Detailed mode: fetch stats subcollections
  const users = [];
  for (const userDoc of snapshot.docs) {
    const userData = userDoc.data();
    const userId = userDoc.id;

    // Fetch crossword stats
    const crosswordStatsRef = doc(db, 'users', userId, 'stats', 'crossword');
    const crosswordStats = await getDoc(crosswordStatsRef);
    const crosswordData = crosswordStats.exists() ? crosswordStats.data() : {};

    // Fetch wordle stats
    const wordleStatsRef = doc(db, 'users', userId, 'stats', 'wordle');
    const wordleStats = await getDoc(wordleStatsRef);
    const wordleData = wordleStats.exists() ? wordleStats.data() : {};

    users.push({
      id: userId,
      name: userData.displayName || userData.username || 'Unknown',
      email: userData.email || 'No email',
      createdAt: userData.createdAt,
      streakCount: userData.streakCount || 0,
      lastActive: userData.lastCompletionDate || crosswordData.lastCompletedAt || wordleData.lastPlayedAt || null,
      totalCrosswords: crosswordData.completedCount || 0,
      totalWordle: wordleData.gamesPlayed || 0,
    });
  }

  return users;
}

export async function getUserDetail(userId) {
  // Get main user document
  const userRef = doc(db, 'users', userId);
  const userDoc = await getDoc(userRef);

  if (!userDoc.exists()) {
    throw new Error('User not found');
  }

  const userData = userDoc.data();

  // Get crossword stats
  const crosswordStatsRef = doc(db, 'users', userId, 'stats', 'crossword');
  const crosswordStatsDoc = await getDoc(crosswordStatsRef);
  const crosswordStats = crosswordStatsDoc.exists() ? crosswordStatsDoc.data() : {};

  // Get wordle stats
  const wordleStatsRef = doc(db, 'users', userId, 'stats', 'wordle');
  const wordleStatsDoc = await getDoc(wordleStatsRef);
  const wordleStats = wordleStatsDoc.exists() ? wordleStatsDoc.data() : {};

  // Get recent crossword activity (last 15 completed puzzles)
  const recentCrosswords = [];
  if (crosswordStats.completedPuzzleIds && Array.isArray(crosswordStats.completedPuzzleIds)) {
    const recentIds = crosswordStats.completedPuzzleIds.slice(-15).reverse();

    for (const puzzleId of recentIds) {
      const progressRef = doc(db, 'users', userId, 'crossword-progress', puzzleId);
      const progressDoc = await getDoc(progressRef);

      if (progressDoc.exists()) {
        const progressData = progressDoc.data();
        recentCrosswords.push({
          id: puzzleId,
          completionTime: progressData.winTime || progressData.totalElapsedMs,
          completedAt: progressData.firstCompletedAt || progressData.lastUpdated,
        });
      }
    }
  }

  // Get recent wordle activity
  const wordleProgressRef = collection(db, 'users', userId, 'wordle-progress');
  const wordleSnapshot = await getDocs(wordleProgressRef);
  const recentWordle = [];

  wordleSnapshot.docs.forEach(doc => {
    const data = doc.data();
    recentWordle.push({
      id: doc.id,
      guessCount: data.guessCount,
      isWon: data.isWon,
      lastUpdated: data.lastUpdated,
    });
  });

  // Sort by date descending and take last 15
  recentWordle.sort((a, b) => {
    const dateA = parseDateId(a.id);
    const dateB = parseDateId(b.id);
    return dateB - dateA;
  });
  const recentWordleSliced = recentWordle.slice(0, 15);

  return {
    id: userId,
    name: userData.displayName || userData.username || 'Unknown',
    email: userData.email || 'No email',
    createdAt: userData.createdAt,
    lastActive: userData.lastCompletionDate || crosswordStats.lastCompletedAt || wordleStats.lastPlayedAt || null,
    streakCount: userData.streakCount || 0,
    streakFreezes: userData.streakFreezes || 0,
    crosswordStats: {
      completedCount: crosswordStats.completedCount || 0,
    },
    wordleStats: {
      gamesPlayed: wordleStats.gamesPlayed || 0,
      gamesWon: wordleStats.gamesWon || 0,
      guessDistribution: wordleStats.guessDistribution || {},
    },
    recentCrosswords,
    recentWordle: recentWordleSliced,
  };
}

function parseDateId(dateId) {
  // Parse date format like "29-01-2026" to Date object
  const parts = dateId.split('-');
  if (parts.length === 3) {
    return new Date(parts[2], parts[1] - 1, parts[0]);
  }
  return new Date(0);
}

export function formatTime(milliseconds) {
  if (!milliseconds) return 'N/A';

  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function formatDate(timestamp) {
  if (!timestamp) return 'Never';

  let date;
  if (timestamp.toDate) {
    // Firestore Timestamp
    date = timestamp.toDate();
  } else if (typeof timestamp === 'number') {
    // Unix timestamp in milliseconds
    date = new Date(timestamp);
  } else {
    return 'Invalid date';
  }

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function formatDateTime(timestamp) {
  if (!timestamp) return 'Never';

  let date;
  if (timestamp.toDate) {
    date = timestamp.toDate();
  } else if (typeof timestamp === 'number') {
    date = new Date(timestamp);
  } else {
    return 'Invalid date';
  }

  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function filterChurnedUsers(users) {
  const fiveDaysAgo = Date.now() - (5 * 24 * 60 * 60 * 1000);

  return users.filter(user => {
    // Check if registered 5+ days ago
    let createdAtMs;
    if (user.createdAt?.toMillis) {
      createdAtMs = user.createdAt.toMillis();
    } else if (typeof user.createdAt === 'number') {
      createdAtMs = user.createdAt;
    } else {
      return false; // Skip users without valid creation date
    }

    if (createdAtMs > fiveDaysAgo) {
      return false; // Too recent
    }

    // Check crossword and wordle counts (need detailed stats)
    const crosswordCount = user.totalCrosswords || 0;
    const wordleCount = user.totalWordle || 0;

    return crosswordCount <= 1 && wordleCount <= 1;
  });
}

export function exportToCSV(users, filename = 'churned-users.csv') {
  // Create CSV content
  const headers = ['Name', 'Email', 'Signup Date', 'Crosswords Completed', 'Wordle Games'];
  const rows = users.map(user => [
    user.name,
    user.email,
    formatDate(user.createdAt),
    user.totalCrosswords || 0,
    user.totalWordle || 0,
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n');

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
